/**
 * Dependency imports
 */
import React from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';

/**
 * Local imports
 */
import { auth } from '@/firebase/auth';

/**
 * Default export
 */
export const useEmailAuth = () => {
  const [info, setInfo] = React.useState<any>(null);
  const [status, setStatus] = React.useState<
    'idle' | 'pending' | 'complete' | 'error'
  >('idle');

  const parseFirebaseError = (err: FirebaseError) =>
    new Error(err.message.replace(/^Firebase:|\((.*?)\).$/g, '').trim(), {
      cause: err.code,
    });

  const login = async (email: string, password: string) =>
    new Promise<void>((resolve, reject) => {
      setStatus('pending');

      signInWithEmailAndPassword(auth, email, password)
        .then((creds) => {
          // Check if email is verified for email/password users
          if (!creds.user.emailVerified) {
            const isEmailPasswordUser = creds.user.providerData.some(
              provider => provider.providerId === 'password'
            );

            if (isEmailPasswordUser) {
              // Email not verified - return special status with user info
              const error = new Error('Please verify your email address before logging in. Check your inbox for the verification link.', {
                cause: 'auth/email-not-verified'
              });

              setStatus('error');
              setInfo({ error, user: creds.user }); // Pass user object for resend functionality

              reject(error);
              return;
            }
          }

          setStatus('complete');
          setInfo(creds);
          resolve();
        })
        .catch((err) => {
          setStatus('error');
          setInfo(parseFirebaseError(err));
          reject(err);
        });
    });

  return [
    login,
    {
      info,
      status,
    },
  ] as const;
};
